import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { PageContentStateContext } from '@src/contexts/wysiwyg/page_content';
import _flatten from 'lodash/flatten';
import { useContext } from 'react';

import {
  PAGE_CONTENT_ITEM_TYPE_TEXT_BOX,
  PAGE_CONTENT_ITEM_TYPE_IMAGE
} from '../../constants/wysiwyg_editor';

import type {
  TPageContentItem,
  TImageContentItem,
  TTextBoxContentItem
} from '../../types/wysiwyg_editor';

export interface AxisSnappableLocationData {
  minLocation: number;
  maxLocation: number;
  edgeType: string;
}

type AxisSnappableLocations = Record<number, AxisSnappableLocationData>;

export interface SnappableLocations {
  x: AxisSnappableLocations;
  y: AxisSnappableLocations;
}

interface ItemSnapLocation {
  value: number;
  minLocation: number;
  maxLocation: number;
  edgeType: string;
}

function isSnappable(
  item: TPageContentItem
): item is TImageContentItem | TTextBoxContentItem {
  return (
    item.type === PAGE_CONTENT_ITEM_TYPE_IMAGE ||
    item.type === PAGE_CONTENT_ITEM_TYPE_TEXT_BOX
  );
}

function getLocation(
  contentItems: Array<TPageContentItem>,
  exceptId: string,
  finder: (item: TPageContentItem) => Array<ItemSnapLocation>
): AxisSnappableLocations {
  return _flatten(
    contentItems
      .filter(isSnappable)
      .filter((item) => item.id !== exceptId)
      .map(finder)
  ).reduce((locations: AxisSnappableLocations, location) => {
    const { value, minLocation, maxLocation, edgeType } = location;
    const matchingLocation = locations[value] || {
      minLocation,
      maxLocation,
      edgeType
    };

    if (matchingLocation.minLocation > minLocation) {
      matchingLocation.minLocation = minLocation;
    }

    if (matchingLocation.maxLocation < maxLocation) {
      matchingLocation.maxLocation = maxLocation;
    }

    locations[value] = matchingLocation;

    return locations;
  }, {});
}

function getTop(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.y,
    minLocation: location.x,
    maxLocation: location.x + location.width,
    edgeType: 'content'
  };
}

function getMiddle(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.y + location.height / 2,
    minLocation: location.x,
    maxLocation: location.x + location.width,
    edgeType: 'content'
  };
}

function getBottom(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.y + location.height,
    minLocation: location.x,
    maxLocation: location.x + location.width,
    edgeType: 'content'
  };
}

function getLeft(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.x,
    minLocation: location.y,
    maxLocation: location.y + location.height,
    edgeType: 'content'
  };
}

function getCenter(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.x + location.width / 2,
    minLocation: location.y,
    maxLocation: location.y + location.height,
    edgeType: 'content'
  };
}

function getRight(item: TPageContentItem): ItemSnapLocation {
  const location = item.containerLocation;
  return {
    value: location.x + location.width,
    minLocation: location.y,
    maxLocation: location.y + location.height,
    edgeType: 'content'
  };
}

function getX(item: TPageContentItem): Array<ItemSnapLocation> {
  return [getLeft(item), getCenter(item), getRight(item)];
}

function getY(item: TPageContentItem): Array<ItemSnapLocation> {
  return [getTop(item), getMiddle(item), getBottom(item)];
}

function addEdge(
  locations: AxisSnappableLocations,
  location: number,
  dashedLine: boolean,
  minMax: { min: number; max: number }
): void {
  locations[location] = {
    minLocation: minMax.min,
    maxLocation: minMax.max,
    edgeType: dashedLine ? 'document-dashed' : 'document-solid'
  };
}

function useSnappableLocations(exceptId: string): SnappableLocations {
  const { snapEdges } = useContext(WysiwygEditorStateContext);
  const { contentItems } = useContext(PageContentStateContext);
  const xLocations = getLocation(contentItems, exceptId, getX);
  const yLocations = getLocation(contentItems, exceptId, getY);

  addEdge(xLocations, snapEdges.left, true, {
    min: snapEdges.top,
    max: snapEdges.bottom
  });
  addEdge(xLocations, snapEdges.right, true, {
    min: snapEdges.top,
    max: snapEdges.bottom
  });
  addEdge(xLocations, 0.5, false, { min: 0, max: 1 });

  addEdge(yLocations, snapEdges.top, true, {
    min: snapEdges.left,
    max: snapEdges.right
  });
  addEdge(yLocations, snapEdges.bottom, true, {
    min: snapEdges.left,
    max: snapEdges.right
  });
  addEdge(yLocations, 0.5, false, { min: 0, max: 1 });

  return {
    x: xLocations,
    y: yLocations
  };
}

export default useSnappableLocations;
